import type { ChangeEvent, Dispatch, FormEvent, KeyboardEvent, ReactNode, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AppShell } from '../components/AppShell';
import { Icon, type IconName } from '../components/Icon';
import { useAuth } from '../features/auth/AuthProvider';
import { useHasPermission } from '../features/auth/usePermission';
import { StatusChip } from '../components/StatusChip';
import { Button, useActionDialog } from '../components/ui';
import { AssignedAdminsPanel } from '../features/patients/components/AssignedAdminsPanel';
import { PatientPaymentsPanel } from '../features/patients/components/PatientPaymentsPanel';
import { PatientPointsPanel } from '../features/patients/components/PatientPointsPanel';
import { PatientProfile } from '../features/patients/components/PatientProfile';
import { StageEditor } from '../features/patients/components/StageEditor';
import {
  useCreatePatientTreatment,
  useCreateTreatmentStage,
  useDeletePatientToothSpacingMap,
  useDeletePatientTreatment,
  useDeleteTreatmentRecommendationLetter,
  useDeleteTreatmentStage,
  useUpdatePatientTreatment,
  useUpdateTreatmentStage,
  useUploadPatientToothSpacingMap,
  useUploadTreatmentRecommendationLetter,
} from '../features/patients/hooks/usePatientMutations';
import { fetchPatientToothSpacingMapBlob, fetchRecommendationLetterBlob } from '../api/patients';
import {
  validateRecommendationLetterImage,
  validateToothSpacingMapImage,
} from '../utils/fileValidation';
import { usePatient } from '../features/patients/hooks/usePatient';
import { useTreatments } from '../features/patients/hooks/useTreatments';
import { PhotoControlSchedulePanel } from '../features/patients/components/PhotoControlSchedulePanel';
import { PhotoControlReviewPanel } from '../features/patients/components/PhotoControlReviewPanel';
import { ResultPanel } from '../features/patients/components/ResultPanel';
import { ToothSpacingSchedulePanel } from '../features/patients/components/ToothSpacingSchedulePanel';
import { ToothSpacingReviewPanel } from '../features/patients/components/ToothSpacingReviewPanel';
import {
  buildClientStatus,
  type ClientStatusAction,
  type ClientStatusItem,
} from '../features/patients/clientStatus';
import type {
  CycleLabelKind,
  CycleStatus,
  Treatment,
  TreatmentStage,
  TreatmentStatus,
} from '../types/admin';
import styles from './ClientDetailPage.module.scss';

const treatmentStatusOptions: readonly TreatmentStatus[] = ['draft', 'active', 'completed', 'cancelled'];

const MAX_PATIENT_FEATURES = 20;

const MAX_DIAGNOSES = 10;

function findPatientVisibleCycleId(treatments: readonly Treatment[]): string | null {
  const activeTreatment = treatments.find((treatment) => treatment.status === 'active');
  if (!activeTreatment) return null;

  const orderedCycles = [...activeTreatment.stages].sort(
    (left, right) => left.cycle_number - right.cycle_number,
  );
  const firstUnfinishedCycle = orderedCycles.find((cycle) => {
    if (cycle.status === 'completed' || cycle.status === 'cancelled') return false;
    return cycle.steps.length === 0 || cycle.steps.some((step) => step.status !== 'completed');
  });

  return firstUnfinishedCycle?.id ?? orderedCycles.at(-1)?.id ?? null;
}

type ClientWorkspaceSection =
  | 'treatments'
  | 'treatment-data'
  | 'photo-control'
  | 'result'
  | 'tooth-spacing'
  | 'assigned-admins'
  | 'payments'
  | 'points';

const sectionHashes: Record<ClientWorkspaceSection, string> = {
  treatments: 'treatments',
  'treatment-data': 'treatment-data',
  'photo-control': 'photo-control',
  result: 'result',
  'tooth-spacing': 'tooth-spacing',
  'assigned-admins': 'assigned-admins',
  payments: 'payments-panel',
  points: 'points-panel',
};

const workspaceSectionOrder: readonly ClientWorkspaceSection[] = [
  'treatments',
  'treatment-data',
  'photo-control',
  'result',
  'tooth-spacing',
  'assigned-admins',
  'payments',
  'points',
];

function getSectionFromHash(value = ''): ClientWorkspaceSection {
  const hash = value.replace(/^#/, '');
  return (Object.entries(sectionHashes).find(([, value]) => value === hash)?.[0] as ClientWorkspaceSection | undefined)
    ?? 'treatments';
}

function getAccessibleSection(value: string, canViewPoints: boolean, canViewResult = true): ClientWorkspaceSection {
  const section = getSectionFromHash(value);
  return section === 'points' && !canViewPoints || section === 'result' && !canViewResult ? 'treatments' : section;
}

const DIAGNOSIS_OPTIONS: readonly string[] = [
  'Звуження щелеп',
  'Скупченість зубів',
  'Ротації окремих зубів',
  'Дистопія (різців, ікол, інших зубів)',
  'Сагітальна щілина',
  'Треми',
  'Протрузії зубів',
  'Ділянки дизоклюзії',
  'Біпротрузія',
  'Ясенна посмішка',
  'Бруксизм',
  'Адентія',
  'Пародонтологічний статус ясен',
  'Синдром Попова-Годона',
  'Мезіальний прикус',
  'Глибокий прикус',
  'Дистальний прикус',
  'Прямий прикус',
  'Перехресний прикус',
  'Адентія зуба (відсутність)',
  'Виражена крива Шпеє',
  'Конвергенція',
  'Ангуляція зуба',
  'Патологічна стертість',
  'Дивергенція зубів',
  'Конвергенція зубів',
  'Перехресне змикання',
  'Зворотнє змикання',
  'Язикове положення зуба',
  'Скупченість у фронті',
  'Гостра суглобова симптоматика',
  'Ретрузія зуба',
];

export function ClientDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const canViewPoints = useHasPermission('points:view');
  const canViewResult = useHasPermission('photo_controls:view');
  const visibleSectionOrder: readonly ClientWorkspaceSection[] = workspaceSectionOrder.filter(
    (section) => (section !== 'points' || canViewPoints) && (section !== 'result' || canViewResult),
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreateTreatmentOpen, setIsCreateTreatmentOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<ClientWorkspaceSection>(
    () => getAccessibleSection(location.hash, canViewPoints, canViewResult),
  );
  const [visitedSections, setVisitedSections] = useState<ReadonlySet<ClientWorkspaceSection>>(
    () => new Set([getAccessibleSection(location.hash, canViewPoints, canViewResult)]),
  );
  const statusNavigationSequence = useRef(0);
  const [statusNavigationRequest, setStatusNavigationRequest] = useState<{
    readonly treatmentId: string;
    readonly stageId?: string;
    readonly sequence: number;
  } | null>(null);
  const {
    data: patient,
    error: patientError,
    isError: isPatientError,
    isLoading: patientLoading,
  } = usePatient(id ?? '');
  const {
    data: treatments,
    error: treatmentsError,
    isError: isTreatmentsError,
    isLoading: treatmentsLoading,
  } = useTreatments(id ?? '', { enabled: Boolean(patient) });
  const createTreatmentMutation = useCreatePatientTreatment(id ?? '');
  const backTarget = `/clients${location.search}`;
  const treatmentsCount = treatments?.length ?? 0;
  const inProgressCycleIds = new Set(
    treatments?.flatMap((treatment) => treatment.stages)
      .filter((stage) => stage.status === 'in_progress')
      .map((stage) => stage.id) ?? [],
  );
  const patientVisibleCycleId = findPatientVisibleCycleId(treatments ?? []);

  useEffect(() => {
    const section = getAccessibleSection(location.hash, canViewPoints, canViewResult);
    setActiveSection(section);
    setVisitedSections((current) => new Set(current).add(section));
  }, [canViewPoints, canViewResult, location.hash]);

  useEffect(() => {
    const activeTab = document.getElementById(`${activeSection}-tab`);
    if (activeTab && typeof activeTab.scrollIntoView === 'function') {
      activeTab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [activeSection]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    function openPayments() {
      setActiveSection('payments');
      setVisitedSections((current) => new Set(current).add('payments'));
      navigate({ pathname: location.pathname, search: location.search, hash: sectionHashes.payments }, { replace: true });
      requestAnimationFrame(() => {
        document.getElementById('payments-tab')?.focus();
        const target = document.getElementById('payments-panel');
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ block: 'start' });
        }
      });
    }

    function handleHashChange() {
      const section = getAccessibleSection(window.location.hash, canViewPoints);
      setActiveSection(section);
      if (section === 'payments') openPayments();
    }

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('waf:open-payments', openPayments);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('waf:open-payments', openPayments);
    };
  }, [canViewPoints, location.pathname, location.search, navigate]);

  function selectSection(section: ClientWorkspaceSection) {
    setActiveSection(section);
    setVisitedSections((current) => new Set(current).add(section));
    navigate({ pathname: location.pathname, search: location.search, hash: sectionHashes[section] }, { replace: true });
  }

  function handleSectionKeyDown(event: KeyboardEvent<HTMLButtonElement>, section: ClientWorkspaceSection) {
    // Walk the sections that are actually rendered: a permission-gated tab has
    // no button to focus, so including it here would strand Home/End/arrows.
    const order = visibleSectionOrder;
    const currentIndex = order.indexOf(section);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % order.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + order.length) % order.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = order.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextSection = order[nextIndex];
    selectSection(nextSection);
    requestAnimationFrame(() => document.getElementById(`${nextSection}-tab`)?.focus());
  }

  function formatDateTime(value: string): string {
    return new Date(value).toLocaleString(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (patientLoading) {
    return (
      <AppShell>
        <div className={styles.page}>
          <div className={`${styles.skeleton} ${styles.skeletonBreadcrumb}`} />
          <div className={`${styles.skeleton} ${styles.skeletonProfile}`} />
          <div className={`${styles.skeleton} ${styles.skeletonTimeline}`} />
        </div>
      </AppShell>
    );
  }

  if (isPatientError) {
    return (
      <AppShell>
        <div className={styles.notFound}>
          <h2 className={styles.notFoundTitle}>{t('clientDetail.unableToLoadPatient')}</h2>
          <p className={styles.treatmentsError}>
            {patientError instanceof Error ? patientError.message : t('clientDetail.tryAgain')}
          </p>
          <button className={styles.backLink} onClick={() => navigate(backTarget)}>
            {t('clientDetail.backToClients')}
          </button>
        </div>
      </AppShell>
    );
  }

  if (!patient) {
    return (
      <AppShell>
        <div className={styles.notFound}>
          <h2 className={styles.notFoundTitle}>{t('clientDetail.patientNotFound')}</h2>
          <button className={styles.backLink} onClick={() => navigate(backTarget)}>
            {t('clientDetail.backToClients')}
          </button>
        </div>
      </AppShell>
    );
  }

  const clientStatusItems = buildClientStatus(patient, treatments ?? []);
  const visibleClientStatusItems = treatmentsLoading || isTreatmentsError
    ? clientStatusItems.filter((item) => item.id === 'spacing-map')
    : clientStatusItems;

  function openStatusDestination(action: ClientStatusAction) {
    selectSection(action.section);
    if (action.openCreateTreatment) setIsCreateTreatmentOpen(true);
    if (action.treatmentId) {
      statusNavigationSequence.current += 1;
      setStatusNavigationRequest({
        treatmentId: action.treatmentId,
        stageId: action.stageId,
        sequence: statusNavigationSequence.current,
      });
    }

    requestAnimationFrame(() => {
      if (action.stageId) document.getElementById(`stage-tab-${action.stageId}`)?.click();
      requestAnimationFrame(() => {
        const target = document.getElementById(action.targetId ?? `${action.section}-tab`);
        if (!target) return;
        if (action.stageKind && target instanceof HTMLButtonElement) target.click();
        target.focus();
        target.scrollIntoView?.({ block: 'center' });
      });
    });
  }

  return (
    <AppShell>
      <div className={styles.page}>
        <div className={styles.breadcrumbRow}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate(backTarget)}
            aria-label={t('clientDetail.backToClients')}
          >
            <Icon name="arrow-back" size={14} />
          </button>
          <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
            <button type="button" className={styles.breadcrumbLink} onClick={() => navigate('/')}>
              {t('nav.dashboard')}
            </button>
            <span className={styles.breadcrumbSep}>›</span>
            <button type="button" className={styles.breadcrumbLink} onClick={() => navigate(backTarget)}>
              {t('nav.clients')}
            </button>
            <span className={styles.breadcrumbSep}>›</span>
            <span className={styles.breadcrumbCurrent}>
              {patient.first_name} {patient.last_name}
            </span>
          </nav>
        </div>

        <PatientProfile
          patient={patient}
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<Icon name="inbox" size={14} />}
                onClick={() => navigate(`/chats?patient=${patient.id}`)}
              >
                {t('clientDetail.openChat')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                leadingIcon={<Icon name="person-add" size={14} />}
                onClick={() => {
                  selectSection('treatments');
                  setIsCreateTreatmentOpen(true);
                }}
              >
                {t('clientDetail.addTreatment')}
              </Button>
            </>
          }
        />

        <ClientStatusSummary
          items={visibleClientStatusItems}
          isLoading={treatmentsLoading}
          isError={isTreatmentsError}
          onAction={openStatusDestination}
        />

        <div className={styles.workspace}>
          <nav className={styles.sectionTabs} role="tablist" aria-label={t('clientDetail.workspaceNavigation')}>
            {([
              { id: 'treatments', label: t('clientDetail.treatments'), icon: 'people', badge: treatmentsCount || undefined },
              { id: 'treatment-data', label: t('clientDetail.sectionTreatmentData'), icon: 'edit' },
              { id: 'photo-control', label: t('clientDetail.sectionPhotoControl'), icon: 'calendar' },
              { id: 'result', label: i18n.language.startsWith('en') ? 'Result' : 'Результат', icon: 'star' },
              { id: 'tooth-spacing', label: t('clientDetail.sectionToothSpacing'), icon: 'history' },
              { id: 'assigned-admins', label: t('clientDetail.sectionAssignedAdmins'), icon: 'person' },
              { id: 'payments', label: t('clientDetail.sectionPayments'), icon: 'payment' },
              { id: 'points', label: t('clientDetail.sectionPoints'), icon: 'star' },
            ] satisfies readonly { id: ClientWorkspaceSection; label: string; icon: IconName; badge?: number }[])
              .filter((section) => (section.id !== 'points' || canViewPoints) && (section.id !== 'result' || canViewResult))
              .map((section) => (
              <button
                key={section.id}
                id={`${section.id}-tab`}
                type="button"
                className={styles.sectionTab}
                role="tab"
                aria-selected={activeSection === section.id}
                aria-controls={sectionHashes[section.id]}
                tabIndex={activeSection === section.id ? 0 : -1}
                onClick={() => selectSection(section.id)}
                onKeyDown={(event) => handleSectionKeyDown(event, section.id)}
              >
                <Icon name={section.icon} size={15} />
                <span>{section.label}</span>
                {section.badge ? <span className={styles.tabBadge}>{section.badge}</span> : null}
              </button>
            ))}
          </nav>

          <WorkspacePanel
            id="treatments"
            active={activeSection === 'treatments'}
            mounted={visitedSections.has('treatments')}
            title={t('clientDetail.treatments')}
            subtitle={t('clientDetail.sectionTreatmentsSubtitle')}
            icon="people"
          >
            {treatmentsLoading ? (
              <div className={styles.skeletonStack}>
                <div className={`${styles.skeleton} ${styles.skeletonTreatment}`} />
                <div className={`${styles.skeleton} ${styles.skeletonTreatment}`} />
              </div>
            ) : isTreatmentsError ? (
              <p className={styles.treatmentsError}>
                {treatmentsError instanceof Error ? treatmentsError.message : t('clientDetail.unableToLoadTreatments')}
              </p>
            ) : (
              <div className={styles.treatmentWorkspace}>
                {isCreateTreatmentOpen && (
                  <CreateTreatmentCard
                    isSubmitting={createTreatmentMutation.isPending}
                    errorMessage={createTreatmentMutation.error instanceof Error ? createTreatmentMutation.error.message : null}
                    onCancel={() => setIsCreateTreatmentOpen(false)}
                    onSubmit={async (payload) => {
                      await createTreatmentMutation.mutateAsync(payload);
                      setIsCreateTreatmentOpen(false);
                    }}
                  />
                )}
                {treatments && treatments.length > 0 ? (
                  treatments.map((treatment, index) => (
                    <EditableTreatmentCard
                      key={treatment.id}
                      patientId={patient.id}
                      treatment={treatment}
                      formatDateTime={formatDateTime}
                      initiallyCollapsed={index > 0}
                      inProgressCycleIds={inProgressCycleIds}
                      patientVisibleCycleId={patientVisibleCycleId}
                      statusNavigationRequest={
                        statusNavigationRequest?.treatmentId === treatment.id
                          ? statusNavigationRequest
                          : null
                      }
                    />
                  ))
                ) : !isCreateTreatmentOpen ? (
                  <div className={styles.emptyTreatmentState}>
                    <p>{t('clientDetail.noTreatmentsYet')}</p>
                    <span>{t('clientDetail.noTreatmentsHint')}</span>
                  </div>
                ) : null}
              </div>
            )}
          </WorkspacePanel>

          <WorkspacePanel
            id="treatment-data"
            active={activeSection === 'treatment-data'}
            mounted={visitedSections.has('treatment-data')}
            title={t('clientDetail.sectionTreatmentData')}
            subtitle={t('clientDetail.sectionTreatmentDataSubtitle')}
            icon="edit"
          >
            {treatmentsLoading ? (
              <div className={styles.skeletonStack}>
                <div className={`${styles.skeleton} ${styles.skeletonTreatment}`} />
                <div className={`${styles.skeleton} ${styles.skeletonTreatment}`} />
              </div>
            ) : isTreatmentsError ? (
              <p className={styles.treatmentsError}>
                {treatmentsError instanceof Error ? treatmentsError.message : t('clientDetail.unableToLoadTreatments')}
              </p>
            ) : treatments && treatments.length > 0 ? (
              <div className={styles.treatmentWorkspace}>
                {treatments.map((treatment) => (
                  <TreatmentDataCard
                    key={treatment.id}
                    patientId={patient.id}
                    treatment={treatment}
                    formatDateTime={formatDateTime}
                  />
                ))}
              </div>
            ) : (
              <WorkspaceEmptyState />
            )}
          </WorkspacePanel>

          <WorkspacePanel
            id="photo-control"
            active={activeSection === 'photo-control'}
            mounted={visitedSections.has('photo-control')}
            title={t('clientDetail.sectionPhotoControl')}
            subtitle={t('clientDetail.sectionPhotoControlSubtitle')}
            icon="calendar"
          >
            {treatmentsLoading ? (
              <div className={`${styles.skeleton} ${styles.skeletonTreatment}`} />
            ) : isTreatmentsError ? (
              <p className={styles.treatmentsError}>
                {treatmentsError instanceof Error ? treatmentsError.message : t('clientDetail.unableToLoadTreatments')}
              </p>
            ) : treatments && treatments.length > 0 ? (
              <>
                <PhotoControlSchedulePanel
                  patientId={patient.id}
                  treatments={treatments}
                />
                <PhotoControlReviewPanel patientId={patient.id} />
              </>
            ) : (
              <WorkspaceEmptyState />
            )}
          </WorkspacePanel>

          {canViewResult && <WorkspacePanel
            id="result"
            active={activeSection === 'result'}
            mounted={visitedSections.has('result')}
            title={i18n.language.startsWith('en') ? 'Before / after' : 'До / після'}
            subtitle={i18n.language.startsWith('en') ? 'Final photo comparison and review' : 'Порівняння фінальних фото та перевірка'}
            icon="star"
          >
            {treatmentsLoading ? <div className={`${styles.skeleton} ${styles.skeletonTreatment}`} /> : isTreatmentsError ? <p role="alert">{treatmentsError instanceof Error ? treatmentsError.message : t('clientDetail.unableToLoadTreatments')}</p> : <ResultPanel key={new URLSearchParams(location.search).get('result_cycle') ?? 'default'} patientId={patient.id} treatments={treatments ?? []} initialCycleId={new URLSearchParams(location.search).get('result_cycle')} />}
          </WorkspacePanel>}

          <WorkspacePanel
            id="tooth-spacing"
            active={activeSection === 'tooth-spacing'}
            mounted={visitedSections.has('tooth-spacing')}
            title={t('clientDetail.sectionToothSpacing')}
            subtitle={t('clientDetail.sectionToothSpacingSubtitle')}
            icon="history"
          >
            <ToothSpacingMapField
              patientId={patient.id}
              mapUrl={patient.tooth_spacing_map_url}
            />
            {treatmentsLoading ? (
              <div className={`${styles.skeleton} ${styles.skeletonTreatment}`} />
            ) : isTreatmentsError ? (
              <p className={styles.treatmentsError}>
                {treatmentsError instanceof Error ? treatmentsError.message : t('clientDetail.unableToLoadTreatments')}
              </p>
            ) : treatments && treatments.length > 0 ? (
              <>
                <ToothSpacingSchedulePanel
                  patientId={patient.id}
                  treatments={treatments}
                />
                <ToothSpacingReviewPanel patientId={patient.id} />
              </>
            ) : (
              <WorkspaceEmptyState />
            )}
          </WorkspacePanel>

          <WorkspacePanel
            id="assigned-admins"
            active={activeSection === 'assigned-admins'}
            mounted={visitedSections.has('assigned-admins')}
            title={t('clientDetail.sectionAssignedAdmins')}
            subtitle={t('clientDetail.sectionAssignedAdminsSubtitle')}
            icon="person"
          >
            <AssignedAdminsPanel patientId={patient.id} />
          </WorkspacePanel>

          <WorkspacePanel
            id="payments"
            active={activeSection === 'payments'}
            mounted={visitedSections.has('payments')}
            title={t('clientDetail.sectionPayments')}
            subtitle={t('clientDetail.sectionPaymentsSubtitle')}
            icon="payment"
          >
            <PatientPaymentsPanel patientId={patient.id} />
          </WorkspacePanel>

          {canViewPoints ? (
          <WorkspacePanel
            id="points"
            active={activeSection === 'points'}
            mounted={visitedSections.has('points')}
            title={t('clientDetail.sectionPoints')}
            subtitle={t('clientDetail.sectionPointsSubtitle')}
            icon="star"
          >
            <PatientPointsPanel patientId={patient.id} />
          </WorkspacePanel>
          ) : null}
        </div>
      </div>
    </AppShell>
  );
}

type ClientStatusSummaryProps = {
  readonly items: readonly ClientStatusItem[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onAction: (action: ClientStatusAction) => void;
};

function ClientStatusSummary({ items, isLoading, isError, onAction }: ClientStatusSummaryProps) {
  const { t } = useTranslation();
  const completeItems = items.filter((item) => item.state === 'complete');
  const attentionItems = items.filter((item) => item.state === 'attention');

  function itemText(statusItem: ClientStatusItem): string {
    const values = { ...statusItem.values };
    if (typeof values.stage === 'string' && values.stage.startsWith('stageEditor.')) {
      values.stage = t(values.stage);
    }
    return t(`clientDetail.clientStatus.items.${statusItem.key}`, values);
  }

  function renderItem(statusItem: ClientStatusItem) {
    const content = (
      <>
        <span
          className={`${styles.clientStatusIcon} ${
            statusItem.state === 'complete' ? styles.clientStatusIconComplete : styles.clientStatusIconAttention
          }`}
          aria-hidden="true"
        >
          <Icon name={statusItem.state === 'complete' ? 'check' : 'info'} size={14} />
        </span>
        <span>
          <strong>
            {statusItem.state === 'complete'
              ? t('clientDetail.clientStatus.doneLabel')
              : t('clientDetail.clientStatus.todoLabel')}
          </strong>{' '}
          {itemText(statusItem)}
        </span>
        {statusItem.action ? <Icon name="arrow-left" size={13} className={styles.clientStatusArrow} /> : null}
      </>
    );

    return (
      <li key={statusItem.id}>
        {statusItem.action ? (
          <button
            type="button"
            className={styles.clientStatusAction}
            onClick={() => onAction(statusItem.action!)}
          >
            {content}
          </button>
        ) : (
          <div className={styles.clientStatusStatic}>{content}</div>
        )}
      </li>
    );
  }

  return (
    <section className={styles.clientStatus} aria-labelledby="client-status-title">
      <header className={styles.clientStatusHeader}>
        <div>
          <h2 id="client-status-title">{t('clientDetail.clientStatus.title')}</h2>
          <p>{t('clientDetail.clientStatus.subtitle')}</p>
        </div>
        {!isLoading && !isError ? (
          <div className={styles.clientStatusCounts} aria-label={t('clientDetail.clientStatus.countsLabel')}>
            <span className={styles.clientStatusCompleteCount}>
              <Icon name="check" size={13} />
              {t('clientDetail.clientStatus.doneCount', { count: completeItems.length })}
            </span>
            <span className={styles.clientStatusAttentionCount}>
              <Icon name="info" size={13} />
              {t('clientDetail.clientStatus.todoCount', { count: attentionItems.length })}
            </span>
          </div>
        ) : null}
      </header>

      {isLoading ? (
        <p className={styles.clientStatusMessage}>{t('clientDetail.clientStatus.loading')}</p>
      ) : null}
      {isError ? (
        <p className={styles.clientStatusMessage} role="alert">
          {t('clientDetail.clientStatus.unavailable')}
        </p>
      ) : null}
      <div className={styles.clientStatusColumns}>
        <div className={styles.clientStatusGroup}>
          <h3>{t('clientDetail.clientStatus.doneTitle')}</h3>
          {completeItems.length ? (
            <ul>{completeItems.map(renderItem)}</ul>
          ) : (
            <p>{t('clientDetail.clientStatus.noneDone')}</p>
          )}
        </div>
        <div className={styles.clientStatusGroup}>
          <h3>{t('clientDetail.clientStatus.todoTitle')}</h3>
          {attentionItems.length ? (
            <ul>{attentionItems.map(renderItem)}</ul>
          ) : (
            <p>{t('clientDetail.clientStatus.nothingLeft')}</p>
          )}
        </div>
      </div>
    </section>
  );
}

type WorkspacePanelProps = {
  readonly id: ClientWorkspaceSection;
  readonly active: boolean;
  readonly mounted: boolean;
  readonly title: string;
  readonly subtitle: string;
  readonly icon: IconName;
  readonly children: ReactNode;
};

function WorkspacePanel({ id, active, mounted, title, subtitle, icon, children }: WorkspacePanelProps) {
  return (
    <section
      id={sectionHashes[id]}
      className={styles.workspacePanel}
      role="tabpanel"
      aria-labelledby={`${id}-tab`}
      hidden={!active}
    >
      <header className={styles.workspaceHeader}>
        <span className={styles.workspaceIcon} aria-hidden="true">
          <Icon name={icon} size={18} />
        </span>
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </header>
      <div className={styles.workspaceBody} id={`${sectionHashes[id]}-content`}>
        {mounted ? children : null}
      </div>
    </section>
  );
}

function WorkspaceEmptyState() {
  const { t } = useTranslation();
  return (
    <div className={styles.emptyTreatmentState}>
      <p>{t('clientDetail.noTreatmentsYet')}</p>
      <span>{t('clientDetail.noTreatmentsHint')}</span>
    </div>
  );
}

type TreatmentDraft = {
  readonly status: TreatmentStatus;
  readonly diagnosis_titles: readonly string[];
  readonly patient_features: readonly string[];
};

type CreateTreatmentCardProps = {
  readonly isSubmitting: boolean;
  readonly errorMessage: string | null;
  readonly onCancel: () => void;
  readonly onSubmit: (payload: {
    status: TreatmentStatus;
    diagnosis_titles: readonly string[] | null;
    patient_features: readonly string[] | null;
  }) => Promise<void>;
};

function CreateTreatmentCard({ isSubmitting, errorMessage, onCancel, onSubmit }: CreateTreatmentCardProps) {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<TreatmentDraft>({
    status: 'draft',
    diagnosis_titles: [],
    patient_features: [],
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const sanitizedDiagnoses = formState.diagnosis_titles
      .map((d) => d.trim())
      .filter((d) => d !== '');
    const sanitizedFeatures = formState.patient_features
      .map((f) => f.trim())
      .filter((f) => f !== '');
    try {
      await onSubmit({
        status: formState.status,
        diagnosis_titles: sanitizedDiagnoses.length > 0 ? sanitizedDiagnoses : null,
        patient_features: sanitizedFeatures.length > 0 ? sanitizedFeatures : null,
      });
    } catch {
      return;
    }
  }

  return (
    <form className={styles.editorCard} onSubmit={handleSubmit}>
      <div className={styles.cardHeader}>
        <div>
          <h3 className={styles.cardTitle}>{t('clientDetail.newTreatment')}</h3>
          <p className={styles.cardSubtitle}>{t('clientDetail.newTreatmentHint')}</p>
        </div>
        <StatusChip status={formState.status} />
        <Button
          className={styles.createTreatmentClose}
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onCancel}
          aria-label={t('clientDetail.hideTreatmentForm')}
          title={t('clientDetail.hideTreatmentForm')}
        >
          <Icon name="close" size={14} />
        </Button>
      </div>
      <TreatmentSummaryFields
        formState={formState}
        onChange={setFormState}
        statusId="create-treatment-status"
      />
      {errorMessage && <p className={styles.inlineError}>{errorMessage}</p>}
      <div className={styles.cardActions}>
        <Button variant="primary" size="md" type="submit" disabled={isSubmitting}>
          {isSubmitting ? t('clientDetail.creatingTreatment') : t('clientDetail.createTreatment')}
        </Button>
      </div>
    </form>
  );
}

const CYCLE_LABEL_KINDS: readonly CycleLabelKind[] = ['stage', 'correction'];

/**
 * Rail labels for every cycle of a treatment.
 *
 * Cycles are numbered per label kind, so a treatment reads "Етап 1, Корекція 1,
 * Етап 2, Корекція 2" — the ordinal is the position among same-kind cycles, not
 * `cycle_number`.
 */
function buildStageRailLabels(
  stages: readonly TreatmentStage[],
  translate: (key: string, options: { readonly number: number }) => string,
): ReadonlyMap<string, string> {
  let stageCount = 0;
  let correctionCount = 0;
  const labels = new Map<string, string>();
  for (const stage of stages) {
    const isCorrection = stage.cycle_label_kind === 'correction';
    const number = isCorrection ? (correctionCount += 1) : (stageCount += 1);
    labels.set(stage.id, translate(isCorrection ? 'clientDetail.correctionN' : 'clientDetail.stageN', { number }));
  }
  return labels;
}

type StageRailChipProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
  readonly label: string;
  readonly statusLabel: string;
  readonly dotClassName: string;
  readonly isActive: boolean;
  readonly isEditingKind: boolean;
  readonly onSelect: () => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  readonly onStartEditKind: () => void;
  readonly onEndEditKind: () => void;
};

type StageRailDeleteActionProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
  readonly label: string;
};

function StageRailDeleteAction({
  patientId,
  treatmentId,
  stage,
  label,
}: StageRailDeleteActionProps) {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const deleteStageMutation = useDeleteTreatmentStage(patientId, treatmentId, stage.id);

  async function handleDelete() {
    if (deleteStageMutation.isPending) return;

    const confirmed = await confirmAction({
      title: t('clientDetail.deleteStageTitle'),
      message: t('clientDetail.deleteStageConfirm', { label }),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteStageMutation.mutateAsync();
    } catch {
      return;
    }
  }

  return (
    <div className={styles.stageRailDeleteAction}>
      <button
        type="button"
        className={styles.stageDeleteButton}
        onClick={() => void handleDelete()}
        disabled={deleteStageMutation.isPending}
        aria-label={t('clientDetail.deleteStageLabel', { label })}
        title={t('clientDetail.deleteStageLabel', { label })}
      >
        <Icon name="delete" size={13} />
      </button>
      {deleteStageMutation.error instanceof Error && (
        <p className={styles.stageRailActionError} role="alert">
          {deleteStageMutation.error.message}
        </p>
      )}
    </div>
  );
}

/**
 * One chip in the stage rail, renamed in place.
 *
 * A cycle is either a stage or a correction — picking the kind renumbers the
 * whole rail (and the patient app) because each kind counts on its own. The
 * chip carries its own mutation so the rail can edit any cycle without the card
 * hoisting a hook per cycle.
 */
function StageRailChip({
  patientId,
  treatmentId,
  stage,
  label,
  statusLabel,
  dotClassName,
  isActive,
  isEditingKind,
  onSelect,
  onKeyDown,
  onStartEditKind,
  onEndEditKind,
}: StageRailChipProps) {
  const { t } = useTranslation();
  const updateKindMutation = useUpdateTreatmentStage(patientId, treatmentId, stage.id);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const savedKind: CycleLabelKind = stage.cycle_label_kind ?? 'stage';

  useEffect(() => {
    if (!isEditingKind) {
      return;
    }
    updateKindMutation.reset();
    pickerRef.current?.querySelector('button')?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the picker opens
  }, [isEditingKind]);

  async function applyKind(kind: CycleLabelKind) {
    if (kind === savedKind) {
      onEndEditKind();
      return;
    }
    try {
      await updateKindMutation.mutateAsync({ cycle_label_kind: kind });
      onEndEditKind();
    } catch {
      // Keep the picker open so the admin can retry; the message renders below.
    }
  }

  if (isEditingKind) {
    return (
      <div
        ref={pickerRef}
        className={styles.stageKindPicker}
        role="group"
        aria-label={t('clientDetail.renameStageLabel')}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onEndEditKind();
          }
        }}
      >
        {CYCLE_LABEL_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            className={`${styles.stageKindOption} ${kind === savedKind ? styles.stageKindOptionActive : ''}`}
            disabled={updateKindMutation.isPending}
            aria-pressed={kind === savedKind}
            onClick={() => void applyKind(kind)}
          >
            {t(`clientDetail.stageLabelKind.${kind}`)}
          </button>
        ))}
        <button
          type="button"
          className={styles.stageRenameAction}
          disabled={updateKindMutation.isPending}
          onClick={onEndEditKind}
          aria-label={t('clientDetail.cancelStageRename')}
          title={t('clientDetail.cancelStageRename')}
        >
          <Icon name="close" size={13} />
        </button>
        {updateKindMutation.error instanceof Error && (
          <p className={styles.stageRenameError}>{updateKindMutation.error.message}</p>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      id={`stage-tab-${stage.id}`}
      role="tab"
      aria-selected={isActive}
      aria-controls={`stage-panel-${stage.id}`}
      aria-label={`${label} · ${statusLabel}`}
      title={`${label} · ${statusLabel}`}
      tabIndex={isActive ? 0 : -1}
      className={`${styles.stageChip} ${isActive ? styles.stageChipActive : ''}`}
      onClick={onSelect}
      onDoubleClick={onStartEditKind}
      onKeyDown={onKeyDown}
    >
      <span className={`${styles.stageChipDot} ${dotClassName}`} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

type EditableTreatmentCardProps = {
  readonly patientId: string;
  readonly treatment: Treatment;
  readonly formatDateTime: (value: string) => string;
  readonly initiallyCollapsed?: boolean;
  readonly inProgressCycleIds: ReadonlySet<string>;
  readonly patientVisibleCycleId: string | null;
  readonly statusNavigationRequest: {
    readonly stageId?: string;
    readonly sequence: number;
  } | null;
};

function EditableTreatmentCard({
  patientId,
  treatment,
  formatDateTime,
  initiallyCollapsed = false,
  inProgressCycleIds,
  patientVisibleCycleId,
  statusNavigationRequest,
}: EditableTreatmentCardProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = user?.roles.includes('super admin') ?? false;
  const createStageMutation = useCreateTreatmentStage(patientId, treatment.id);
  const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed);
  const [isStagesCollapsed, setIsStagesCollapsed] = useState(false);
  const treatmentTitle = treatment.diagnosis_titles?.length
    ? treatment.diagnosis_titles.join(', ')
    : t('clientDetail.untitledTreatment');
  const stages = treatment.stages;
  const defaultStage = stages.find((stage) => stage.status === 'in_progress') ?? stages[stages.length - 1] ?? null;
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const activeStage = stages.find((stage) => stage.id === selectedStageId) ?? defaultStage;
  // The rail doubles as the rename surface, so it shows even for a single stage.
  const showStageRail = stages.length > 0;
  const [labelKindStageId, setLabelKindStageId] = useState<string | null>(null);
  const cycleStatusLabels: Record<CycleStatus, string> = {
    draft: t('stageEditor.cycleStatus.draft'),
    in_progress: t('stageEditor.cycleStatus.in_progress'),
    completed: t('stageEditor.cycleStatus.completed'),
    cancelled: t('stageEditor.cycleStatus.cancelled'),
  };
  const stageDotClasses: Record<CycleStatus, string> = {
    draft: styles.stageChipDotDraft,
    in_progress: styles.stageChipDotInProgress,
    completed: styles.stageChipDotCompleted,
    cancelled: styles.stageChipDotCancelled,
  };
  const treatmentContentId = `treatment-${treatment.id}-content`;
  const stagesContentId = `treatment-${treatment.id}-stages`;
  const stagesContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!statusNavigationRequest) return;
    setIsCollapsed(false);
    setIsStagesCollapsed(false);
    if (statusNavigationRequest.stageId) setSelectedStageId(statusNavigationRequest.stageId);
  }, [statusNavigationRequest]);

  // Selecting another stage swaps the panel below the rail: bring the rail back into view so the
  // admin never lands mid-way through the newly rendered stage.
  function selectStage(stageId: string) {
    if (stageId === activeStage?.id) {
      return;
    }
    setLabelKindStageId(null);
    setSelectedStageId(stageId);
    requestAnimationFrame(() => stagesContentRef.current?.scrollIntoView?.({ block: 'start' }));
  }

  const stageRailLabels = buildStageRailLabels(stages, t);

  function stageRailLabel(stage: Treatment['stages'][number]): string {
    return stageRailLabels.get(stage.id) ?? t('clientDetail.stageN', { number: stage.cycle_number });
  }

  function handleStageRailKeyDown(event: KeyboardEvent<HTMLButtonElement>, stageIndex: number) {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (stageIndex + 1) % stages.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (stageIndex - 1 + stages.length) % stages.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = stages.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextStage = stages[nextIndex];
    selectStage(nextStage.id);
    requestAnimationFrame(() => document.getElementById(`stage-tab-${nextStage.id}`)?.focus());
  }

  async function handleAddPreparingStage() {
    try {
      const created = await createStageMutation.mutateAsync({
        current_stage_kind: 'preparing',
      });
      setSelectedStageId(created?.id ?? null);
    } catch {
      return;
    }
  }

  return (
    <section className={styles.editorCard}>
      <div className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitleRow}>
            <h3 className={styles.cardTitle}>{treatmentTitle}</h3>
            <StatusChip status={treatment.status} />
          </div>
          <p className={styles.cardSubtitle}>{t('clientDetail.createdAt', { datetime: formatDateTime(treatment.created_at) })}</p>
        </div>
        <div className={styles.cardHeaderActions}>
          <button
            type="button"
            className={`${styles.collapseButton} ${isCollapsed ? styles.collapseButtonCollapsed : ''}`}
            onClick={() => setIsCollapsed((prev) => !prev)}
            aria-label={t('clientDetail.toggleTreatment', { defaultValue: 'Розгорнути або згорнути розділ' })}
            aria-expanded={!isCollapsed}
            aria-controls={!isCollapsed ? treatmentContentId : undefined}
          >
            <Icon name="chevron-down" size={14} />
          </button>
        </div>
      </div>

      {!isCollapsed && <div id={treatmentContentId} className={styles.treatmentContent}>
        <div className={styles.cardSection}>
        <div className={styles.sectionTitleRow}>
          <h4 className={styles.sectionTitle}>{t('clientDetail.stages')}</h4>
          <span className={styles.sectionHint}>
            {t('clientDetail.stagesCount', { count: stages.length })}
          </span>
          {!isStagesCollapsed && (
            <Button
              id={`treatment-${treatment.id}-add-stage`}
              variant="secondary"
              size="sm"
              type="button"
              disabled={createStageMutation.isPending}
              onClick={() => void handleAddPreparingStage()}
            >
              {createStageMutation.isPending ? t('clientDetail.addingStage') : t('clientDetail.addStage')}
            </Button>
          )}
          <div className={styles.cardHeaderActions}>
            <button
              type="button"
              className={`${styles.collapseButton} ${isStagesCollapsed ? styles.collapseButtonCollapsed : ''}`}
              onClick={() => setIsStagesCollapsed((prev) => !prev)}
              aria-label={t('clientDetail.toggleStages', { defaultValue: 'Розгорнути або згорнути етапи' })}
              aria-expanded={!isStagesCollapsed}
              aria-controls={!isStagesCollapsed ? stagesContentId : undefined}
            >
              <Icon name="chevron-down" size={14} />
            </button>
          </div>
        </div>
        {!isStagesCollapsed && (
          <div id={stagesContentId} className={styles.stagesContent} ref={stagesContentRef}>
            {createStageMutation.error instanceof Error && (
              <p className={styles.inlineError}>{createStageMutation.error.message}</p>
            )}
            {activeStage ? (
              <>
                {showStageRail && (
                  <div className={styles.stageRail}>
                    <div className={styles.stageRailTabs}>
                      <div
                        className={styles.stageRailTabList}
                        role="tablist"
                        aria-label={t('clientDetail.stageSelectorLabel')}
                      >
                        {stages.map((stage, index) => {
                          const stageStatus: CycleStatus = stage.status ?? 'in_progress';
                          const isActive = stage.id === activeStage.id;
                          return (
                            <StageRailChip
                              key={stage.id}
                              patientId={patientId}
                              treatmentId={treatment.id}
                              stage={stage}
                              label={stageRailLabel(stage)}
                              statusLabel={cycleStatusLabels[stageStatus]}
                              dotClassName={stageDotClasses[stageStatus]}
                              isActive={isActive}
                              isEditingKind={stage.id === labelKindStageId}
                              onSelect={() => selectStage(stage.id)}
                              onKeyDown={(event) => handleStageRailKeyDown(event, index)}
                              onStartEditKind={() => setLabelKindStageId(stage.id)}
                              onEndEditKind={() => setLabelKindStageId(null)}
                            />
                          );
                        })}
                      </div>
                      {labelKindStageId === null && (
                        <div className={styles.stageRailActions}>
                          <button
                            type="button"
                            className={styles.stageRenameButton}
                            onClick={() => setLabelKindStageId(activeStage.id)}
                            aria-label={t('clientDetail.renameStage')}
                            title={t('clientDetail.renameStage')}
                          >
                            <Icon name="edit" size={13} />
                          </button>
                          {isSuperAdmin && (
                            <StageRailDeleteAction
                              patientId={patientId}
                              treatmentId={treatment.id}
                              stage={activeStage}
                              label={stageRailLabel(activeStage)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div
                  id={`stage-panel-${activeStage.id}`}
                  role={showStageRail ? 'tabpanel' : undefined}
                  aria-labelledby={showStageRail ? `stage-tab-${activeStage.id}` : undefined}
                >
                  <StageEditor
                    key={activeStage.id}
                    patientId={patientId}
                    treatmentId={treatment.id}
                    stage={activeStage}
                    hasNextCycle={treatment.stages.some(
                      (cycle) => cycle.cycle_number > activeStage.cycle_number
                        && (cycle.status === 'draft' || cycle.status === 'in_progress'),
                    )}
                    isPatientVisibleCycle={activeStage.id === patientVisibleCycleId}
                    hasOtherInProgressCycle={
                      inProgressCycleIds.size > (inProgressCycleIds.has(activeStage.id) ? 1 : 0)
                    }
                  />
                </div>
              </>
            ) : (
              <p className={styles.emptyHint}>{t('clientDetail.noStagesYet')}</p>
            )}
          </div>
        )}
        </div>
      </div>}
    </section>
  );
}

type TreatmentDataCardProps = {
  readonly patientId: string;
  readonly treatment: Treatment;
  readonly formatDateTime: (value: string) => string;
};

function TreatmentDataCard({ patientId, treatment, formatDateTime }: TreatmentDataCardProps) {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const updateTreatmentMutation = useUpdatePatientTreatment(patientId, treatment.id);
  const deleteTreatmentMutation = useDeletePatientTreatment(patientId, treatment.id);
  const [formState, setFormState] = useState<TreatmentDraft>({
    status: treatment.status,
    diagnosis_titles: treatment.diagnosis_titles ? [...treatment.diagnosis_titles] : [],
    patient_features: treatment.patient_features ? [...treatment.patient_features] : [],
  });
  const treatmentTitle = treatment.diagnosis_titles?.length
    ? treatment.diagnosis_titles.join(', ')
    : t('clientDetail.untitledTreatment');

  async function handleDeleteTreatment() {
    if (!(await confirmAction({
      title: t('clientDetail.deleteTreatmentTitle'),
      message: t('clientDetail.deleteTreatmentConfirm', { name: treatmentTitle }),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    }))) {
      return;
    }
    try {
      await deleteTreatmentMutation.mutateAsync();
    } catch {
      return;
    }
  }

  async function handleTreatmentSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const sanitizedDiagnoses = formState.diagnosis_titles
      .map((diagnosis) => diagnosis.trim())
      .filter((diagnosis) => diagnosis !== '');
    const sanitizedFeatures = formState.patient_features
      .map((feature) => feature.trim())
      .filter((feature) => feature !== '');
    try {
      await updateTreatmentMutation.mutateAsync({
        status: formState.status,
        diagnosis_titles: sanitizedDiagnoses.length > 0 ? sanitizedDiagnoses : null,
        patient_features: sanitizedFeatures.length > 0 ? sanitizedFeatures : null,
      });
    } catch {
      return;
    }
  }

  return (
    <section className={styles.editorCard}>
      <div className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitleRow}>
            <h3 className={styles.cardTitle}>{treatmentTitle}</h3>
            <StatusChip status={treatment.status} />
          </div>
          <p className={styles.cardSubtitle}>
            {t('clientDetail.createdAt', { datetime: formatDateTime(treatment.created_at) })}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          leadingIcon={<Icon name="delete" size={14} />}
          onClick={() => void handleDeleteTreatment()}
          disabled={deleteTreatmentMutation.isPending}
        >
          {t('clientDetail.deleteTreatmentTitle')}
        </Button>
      </div>

      <form className={styles.cardSection} onSubmit={handleTreatmentSave}>
        <TreatmentSummaryFields
          formState={formState}
          onChange={setFormState}
          statusId={`treatment-${treatment.id}-status-action`}
          diagnosisActionId={`treatment-${treatment.id}-diagnosis-action`}
        />
        {updateTreatmentMutation.error instanceof Error && (
          <p className={styles.inlineError}>{updateTreatmentMutation.error.message}</p>
        )}
        {deleteTreatmentMutation.error instanceof Error && (
          <p className={styles.inlineError}>{deleteTreatmentMutation.error.message}</p>
        )}
        <div className={styles.cardActions}>
          <Button variant="primary" size="md" type="submit" disabled={updateTreatmentMutation.isPending}>
            {updateTreatmentMutation.isPending ? t('clientDetail.savingTreatment') : t('clientDetail.saveTreatmentSummary')}
          </Button>
        </div>
      </form>

      <div className={styles.divider} />
      <RecommendationLetterField patientId={patientId} treatment={treatment} />
    </section>
  );
}

type TreatmentSummaryFieldsProps = {
  readonly formState: TreatmentDraft;
  readonly onChange: Dispatch<SetStateAction<TreatmentDraft>>;
  readonly statusId?: string;
  readonly diagnosisActionId?: string;
};

function TreatmentSummaryFields({
  formState,
  onChange,
  statusId,
  diagnosisActionId,
}: TreatmentSummaryFieldsProps) {
  const { t } = useTranslation();

  const statusLabels: Record<TreatmentStatus, string> = {
    draft: t('status.draft'),
    active: t('status.active'),
    completed: t('status.completed'),
    cancelled: t('status.cancelled'),
  };

  return (
    <div className={styles.formGrid}>
      <label className={styles.field}>
        <span>{t('clientDetail.treatmentStatus')}</span>
        <select
          id={statusId}
          value={formState.status}
          onChange={(event) =>
            onChange((current) => ({ ...current, status: event.target.value as TreatmentStatus }))
          }
        >
          {treatmentStatusOptions.map((option) => (
            <option key={option} value={option}>{statusLabels[option]}</option>
          ))}
        </select>
      </label>
      <fieldset className={`${styles.field} ${styles.fieldSpanTwo} ${styles.repeatableFieldset}`}>
        <legend>{t('clientDetail.diagnoses')}</legend>
        <div className={styles.repeatableFields}>
          {formState.diagnosis_titles.map((diagnosis, index) => (
            <div key={index} className={styles.repeatableRow}>
              <select
                aria-label={`${t('clientDetail.diagnoses')} ${index + 1}`}
                className={styles.repeatableControl}
                value={diagnosis}
                onChange={(event) =>
                  onChange((current) => ({
                    ...current,
                    diagnosis_titles: current.diagnosis_titles.map((d, i) =>
                      i === index ? event.target.value : d,
                    ),
                  }))
                }
              >
                <option value="">—</option>
                {DIAGNOSIS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
                {diagnosis && !DIAGNOSIS_OPTIONS.includes(diagnosis) && (
                  <option value={diagnosis}>{diagnosis}</option>
                )}
              </select>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                iconOnly
                className={styles.repeatableRemove}
                onClick={() =>
                  onChange((current) => ({
                    ...current,
                    diagnosis_titles: current.diagnosis_titles.filter((_, i) => i !== index),
                  }))
                }
                aria-label={`${t('clientDetail.removeDiagnosis')} ${index + 1}`}
              >
                <Icon name="close" size={14} />
              </Button>
            </div>
          ))}
          {formState.diagnosis_titles.length < MAX_DIAGNOSES && (
            <Button
              id={diagnosisActionId}
              type="button"
              variant="secondary"
              size="sm"
              className={styles.addFieldButton}
              onClick={() =>
                onChange((current) => ({
                  ...current,
                  diagnosis_titles: [...current.diagnosis_titles, ''],
                }))
              }
              aria-label={t('clientDetail.addDiagnosis')}
            >
              {t('clientDetail.addDiagnosis')}
            </Button>
          )}
        </div>
      </fieldset>
      <fieldset className={`${styles.field} ${styles.fieldSpanTwo} ${styles.repeatableFieldset}`}>
        <legend>{t('clientDetail.patientFeatures')}</legend>
        <div className={styles.repeatableFields}>
          {formState.patient_features.map((feature, index) => (
            <div key={index} className={styles.repeatableRow}>
              <input
                aria-label={`${t('clientDetail.patientFeatures')} ${index + 1}`}
                className={styles.repeatableControl}
                type="text"
                value={feature}
                onChange={(event) =>
                  onChange((current) => ({
                    ...current,
                    patient_features: current.patient_features.map((f, i) =>
                      i === index ? event.target.value : f,
                    ),
                  }))
                }
                placeholder={t('clientDetail.patientFeaturePlaceholder')}
                maxLength={200}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                iconOnly
                className={styles.repeatableRemove}
                onClick={() =>
                  onChange((current) => ({
                    ...current,
                    patient_features: current.patient_features.filter((_, i) => i !== index),
                  }))
                }
                aria-label={`${t('clientDetail.removePatientFeature')} ${index + 1}`}
              >
                <Icon name="close" size={14} />
              </Button>
            </div>
          ))}
          {formState.patient_features.length < MAX_PATIENT_FEATURES && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className={styles.addFieldButton}
              onClick={() =>
                onChange((current) => ({
                  ...current,
                  patient_features: [...current.patient_features, ''],
                }))
              }
            >
              {t('clientDetail.addPatientFeature')}
            </Button>
          )}
        </div>
      </fieldset>
    </div>
  );
}

type RecommendationLetterFieldProps = {
  readonly patientId: string;
  readonly treatment: Treatment;
};

function RecommendationLetterField({ patientId, treatment }: RecommendationLetterFieldProps) {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const { tokens } = useAuth();
  const accessToken = tokens?.access_token ?? null;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const uploadMutation = useUploadTreatmentRecommendationLetter(patientId, treatment.id);
  const deleteMutation = useDeleteTreatmentRecommendationLetter(patientId, treatment.id);

  const hasLetter = Boolean(treatment.recommendation_letter_url);

  useEffect(() => {
    if (!hasLetter || !accessToken) {
      setPreviewUrl(null);
      setPreviewError(null);
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    setPreviewError(null);
    fetchRecommendationLetterBlob(patientId, treatment.id, accessToken)
      .then((blob) => {
        if (cancelled) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setPreviewUrl(createdUrl);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        setPreviewError(
          error instanceof Error ? error.message : t('clientDetail.recommendationLetterPreviewLoadFailed'),
        );
      });

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [accessToken, hasLetter, patientId, treatment.id, treatment.updated_at, t]);

  function openFilePicker() {
    setValidationError(null);
    fileInputRef.current?.click();
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) {
      return;
    }

    const error = validateRecommendationLetterImage(file);
    if (error === 'type') {
      setValidationError(t('clientDetail.recommendationLetterInvalidType'));
      return;
    }
    if (error === 'size') {
      setValidationError(t('clientDetail.recommendationLetterTooLarge'));
      return;
    }

    setValidationError(null);
    try {
      await uploadMutation.mutateAsync(file);
    } catch {
      return;
    }
  }

  async function handleDelete() {
    if (!(await confirmAction({
      message: t('clientDetail.recommendationLetterDeleteConfirm'),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    }))) {
      return;
    }
    try {
      await deleteMutation.mutateAsync();
    } catch {
      return;
    }
  }

  const uploadError = uploadMutation.error instanceof Error ? uploadMutation.error.message : null;
  const deleteError = deleteMutation.error instanceof Error ? deleteMutation.error.message : null;

  return (
    <div className={styles.cardSection}>
      <div className={styles.sectionTitleRow}>
        <h4 className={styles.sectionTitle}>{t('clientDetail.recommendationLetter')}</h4>
      </div>

      {hasLetter ? (
        <div className={styles.recommendationLetterPreview}>
          {previewUrl ? (
            <img src={previewUrl} alt={t('clientDetail.recommendationLetter')} />
          ) : previewError ? (
            <p className={styles.inlineError}>{previewError}</p>
          ) : (
            <p className={styles.emptyHint}>{t('clientDetail.recommendationLetterUploading')}</p>
          )}
          <div className={styles.cardActions}>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              disabled={uploadMutation.isPending || deleteMutation.isPending}
              onClick={openFilePicker}
            >
              {uploadMutation.isPending
                ? t('clientDetail.recommendationLetterUploading')
                : t('clientDetail.recommendationLetterReplaceCta')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              disabled={uploadMutation.isPending || deleteMutation.isPending}
              onClick={handleDelete}
            >
              {deleteMutation.isPending
                ? t('clientDetail.recommendationLetterDeleting')
                : t('clientDetail.recommendationLetterDeleteCta')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.cardActions}>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            disabled={uploadMutation.isPending}
            onClick={openFilePicker}
          >
            {uploadMutation.isPending
              ? t('clientDetail.recommendationLetterUploading')
              : t('clientDetail.recommendationLetterUploadCta')}
          </Button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={handleFileChange}
      />

      {validationError && <p className={styles.inlineError}>{validationError}</p>}
      {uploadError && <p className={styles.inlineError}>{uploadError}</p>}
      {deleteError && <p className={styles.inlineError}>{deleteError}</p>}
    </div>
  );
}

type ToothSpacingMapFieldProps = {
  readonly patientId: string;
  readonly mapUrl: string | null;
};

function ToothSpacingMapField({ patientId, mapUrl }: ToothSpacingMapFieldProps) {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const { tokens } = useAuth();
  const accessToken = tokens?.access_token ?? null;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const uploadMutation = useUploadPatientToothSpacingMap(patientId);
  const deleteMutation = useDeletePatientToothSpacingMap(patientId);

  const hasMap = Boolean(mapUrl);

  useEffect(() => {
    if (!hasMap || !accessToken) {
      setPreviewUrl(null);
      setPreviewError(null);
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    setPreviewError(null);
    fetchPatientToothSpacingMapBlob(patientId, accessToken)
      .then((blob) => {
        if (cancelled) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setPreviewUrl(createdUrl);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        setPreviewError(
          error instanceof Error ? error.message : t('clientDetail.toothSpacingMapPreviewLoadFailed'),
        );
      });

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [accessToken, hasMap, mapUrl, patientId, t]);

  function openFilePicker() {
    setValidationError(null);
    fileInputRef.current?.click();
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) {
      return;
    }

    const error = validateToothSpacingMapImage(file);
    if (error === 'type') {
      setValidationError(t('clientDetail.toothSpacingMapInvalidType'));
      return;
    }
    if (error === 'size') {
      setValidationError(t('clientDetail.toothSpacingMapTooLarge'));
      return;
    }

    setValidationError(null);
    try {
      await uploadMutation.mutateAsync(file);
    } catch {
      return;
    }
  }

  async function handleDelete() {
    if (!(await confirmAction({
      message: t('clientDetail.toothSpacingMapDeleteConfirm'),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    }))) {
      return;
    }
    try {
      await deleteMutation.mutateAsync();
    } catch {
      return;
    }
  }

  const uploadError = uploadMutation.error instanceof Error ? uploadMutation.error.message : null;
  const deleteError = deleteMutation.error instanceof Error ? deleteMutation.error.message : null;

  return (
    <div className={styles.cardSection}>
      <div className={styles.sectionTitleRow}>
        <h4 className={styles.sectionTitle}>{t('clientDetail.toothSpacingMap')}</h4>
      </div>

      {hasMap ? (
        <div className={styles.recommendationLetterPreview}>
          {previewUrl ? (
            <img src={previewUrl} alt={t('clientDetail.toothSpacingMap')} />
          ) : previewError ? (
            <p className={styles.inlineError} role="alert">{previewError}</p>
          ) : (
            <p className={styles.emptyHint}>{t('clientDetail.toothSpacingMapUploading')}</p>
          )}
          <div className={styles.cardActions}>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              disabled={uploadMutation.isPending || deleteMutation.isPending}
              onClick={openFilePicker}
            >
              {uploadMutation.isPending
                ? t('clientDetail.toothSpacingMapUploading')
                : t('clientDetail.toothSpacingMapReplaceCta')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              disabled={uploadMutation.isPending || deleteMutation.isPending}
              onClick={handleDelete}
            >
              {deleteMutation.isPending
                ? t('clientDetail.toothSpacingMapDeleting')
                : t('clientDetail.toothSpacingMapDeleteCta')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.toothSpacingMapEmpty}>
          <span className={styles.toothSpacingMapEmptyIcon} aria-hidden="true">
            <Icon name="attach" size={18} />
          </span>
          <div className={styles.toothSpacingMapEmptyCopy}>
            <span className={styles.requiredBadge}>{t('clientDetail.requiredBadge')}</span>
            <strong>{t('clientDetail.toothSpacingMapMissingTitle')}</strong>
            <p>{t('clientDetail.toothSpacingMapMissingHint')}</p>
          </div>
          <Button
            id="tooth-spacing-map-upload"
            variant="secondary"
            size="sm"
            type="button"
            leadingIcon={<Icon name="attach" size={14} />}
            className={styles.toothSpacingMapUploadButton}
            disabled={uploadMutation.isPending}
            onClick={openFilePicker}
          >
            {uploadMutation.isPending
              ? t('clientDetail.toothSpacingMapUploading')
              : t('clientDetail.toothSpacingMapUploadCta')}
          </Button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={handleFileChange}
      />

      {validationError && <p className={styles.inlineError} role="alert">{validationError}</p>}
      {uploadError && <p className={styles.inlineError} role="alert">{uploadError}</p>}
      {deleteError && <p className={styles.inlineError} role="alert">{deleteError}</p>}
    </div>
  );
}
